<template>
	<SuiSelectListHelpers
		class="user-assign-select__group-wrapper"
		:data-role-id="group.role?.id"
		@click="handleGroupClick"
	>
		<SuiSelectListHelpersSelectAll
			v-if="!isSingleUserAssign && !isRoleFilterActive"
			:checked="group.isAllUsersSelected && !group.isRoleSelectable"
			select-all-text=""
			:aria-label="
				$t('user_assign_select_all_by_group_label', {
					name: group.name,
				})
			"
			@input="$emit('toggle-all-users-selected', group, $event)"
		/>
		<h3 class="user-assign-select__group-name">
			{{ group.name }}
		</h3>
		<SuiIcon
			name="icon-arrow-down-16"
			class="user-assign-select__expand-icon"
			:class="{
				'user-assign-select__expand-icon--rotate': isClosed,
			}"
		/>
	</SuiSelectListHelpers>

	<div v-if="!isClosed" role="group" :aria-label="group.name">
		<UserAssignSelectListRoleItem
			v-if="group.role && group.isRoleSelectable"
			:role="group.role"
			:is-selected="isRoleSelected"
			@click="$emit('role-clicked', $event)"
		/>
		<UserAssignSelectListUserItem
			v-for="user in group.userList"
			:key="user.id"
			:user="user"
			:is-selected="selectedUsers.includes(user.id)"
			:task-users-availability="taskUsersAvailability"
			:group-id="group.id"
			@click="$emit('user-clicked', $event)"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
	SuiSelectListHelpers,
	SuiSelectListHelpersSelectAll,
	SuiIcon,
} from '@developers/scoro-ui';
import {
	type User,
	type UserAssignSelectAvailabilityQuery,
} from '@/generated/graphql';
import UserAssignSelectListUserItem from './UserAssignSelectListUserItem.vue';
import UserAssignSelectListRoleItem from './UserAssignSelectListRoleItem.vue';
import type { UserAssignGroup, UserAssignSelectRole } from '../../../types';

export interface UserAssignSelectListGroupProps {
	group: UserAssignGroup;
	isClosed: boolean;
	selectedUsers: Array<User['id']>;
	selectedRoles: UserAssignSelectRole[];
	isSingleUserAssign: boolean;
	taskUsersAvailability: UserAssignSelectAvailabilityQuery['TaskUsersAvailability'];
	activeRoleFilterId: UserAssignSelectRole['id'] | null;
}

const { group, selectedRoles, activeRoleFilterId } =
	defineProps<UserAssignSelectListGroupProps>();

const emit = defineEmits<{
	'toggle-group-dropdown': [target: HTMLElement, group: UserAssignGroup];
	'toggle-all-users-selected': [group: UserAssignGroup, value: boolean];
	'user-clicked': [userId: User['id']];
	'role-clicked': [role: UserAssignSelectRole];
}>();

const isRoleSelected = computed(() => {
	return selectedRoles.some((r) => r?.id === group.role?.id);
});

const isRoleFilterActive = computed(() => {
	return activeRoleFilterId !== null;
});

const handleGroupClick = (event: MouseEvent) => {
	emit('toggle-group-dropdown', event.target as HTMLElement, group);
};
</script>
